// @ts-check

/** Listed in Join Room for anyone. */
export const ROOM_VISIBILITY_PUBLIC = 'public';

/** Joinable by room code only; omitted from the public list. */
export const ROOM_VISIBILITY_UNLISTED = 'unlisted';

/** Maximum characters accepted for a room password (plaintext, not a secret). */
export const MAX_ROOM_PASSWORD_LENGTH = 12;

/**
 * Room passwords are plaintext codes: uppercase A–Z and 0–9 only.
 * Comparison is case-insensitive because the value is always capitalised.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeRoomPassword(value) {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, MAX_ROOM_PASSWORD_LENGTH);
}

/**
 * @param {unknown} value
 * @returns {typeof ROOM_VISIBILITY_PUBLIC | typeof ROOM_VISIBILITY_UNLISTED}
 */
export function normalizeRoomVisibility(value) {
  return String(value || '').trim().toLowerCase() === ROOM_VISIBILITY_UNLISTED
    ? ROOM_VISIBILITY_UNLISTED
    : ROOM_VISIBILITY_PUBLIC;
}

/**
 * Web rooms default to public. Discord Activity rooms are always unlisted.
 * Unlisted is only honoured when the operator flag is on, unless `forceUnlisted`
 * is set (Kids web rooms stay unlisted even when the flag is off).
 *
 * @param {{
 *   isActivity?: boolean,
 *   requested?: unknown,
 *   allowUnlisted?: boolean,
 *   forceUnlisted?: boolean
 * }} options
 * @returns {typeof ROOM_VISIBILITY_PUBLIC | typeof ROOM_VISIBILITY_UNLISTED}
 */
export function resolveRoomVisibility(options = {}) {
  if (options.isActivity) return ROOM_VISIBILITY_UNLISTED;
  if (options.forceUnlisted) return ROOM_VISIBILITY_UNLISTED;
  if (options.allowUnlisted && normalizeRoomVisibility(options.requested) === ROOM_VISIBILITY_UNLISTED) {
    return ROOM_VISIBILITY_UNLISTED;
  }
  return ROOM_VISIBILITY_PUBLIC;
}

/**
 * True when a D1 row is a web public room (not a Discord Activity room).
 *
 * Discord rooms stay unlisted even before `instance_id` is stamped. A public
 * row with no instance is what Join lists and what History grays Delete on.
 *
 * @param {Record<string, any>|null|undefined} row
 * @returns {boolean}
 */
export function isWebPublicRoom(row) {
  if (!row) return false;
  if (String(row.instance_id || '').trim()) return false;
  return normalizeRoomVisibility(row.visibility) === ROOM_VISIBILITY_PUBLIC;
}

/**
 * True when a live room (Durable Object state or D1 row) may accept a
 * non-admin public spectator. Unlisted and passworded rooms stay admin-silent only.
 *
 * @param {Record<string, any>|null|undefined} room
 * @returns {boolean}
 */
export function roomAllowsPublicSpectators(room) {
  if (!room) return false;
  if (normalizeRoomVisibility(room.visibility) !== ROOM_VISIBILITY_PUBLIC) return false;
  if (room.hasPassword === true || room.has_password === true || room.has_password === 1 || room.has_password === '1') {
    return false;
  }
  if (String(room.passwordHash || room.password_hash || '').trim()) return false;
  return true;
}

/**
 * True when a D1 room row should appear in the public Join list.
 *
 * A published Discord Activity room is listed once its visibility is explicitly
 * `public`. A blank visibility still means public only for a web room (no instance).
 * A password hash keeps the room off the list.
 *
 * @param {Record<string, any>|null|undefined} row
 * @returns {boolean}
 */
export function isListedPublicRoom(row) {
  if (!row) return false;
  if (Number(row.is_active) === 0 || row.is_completed) return false;
  if (row.deleted_at) return false;
  if (roomRowHasPassword(row)) return false;
  const instanceId = String(row.instance_id || '').trim();
  const raw = String(row.visibility ?? '').trim().toLowerCase();
  if (instanceId) return raw === ROOM_VISIBILITY_PUBLIC;
  return normalizeRoomVisibility(row.visibility) === ROOM_VISIBILITY_PUBLIC;
}

/**
 * @param {Record<string, any>|null|undefined} row
 * @returns {boolean}
 */
export function roomRowHasPassword(row) {
  if (!row) return false;
  if (row.has_password === true || row.has_password === 1 || row.has_password === '1') return true;
  return !!String(row.password_hash || '').trim();
}

/**
 * Header / admin icon: public, private (unlisted), or locked (password).
 * A password wins over unlisted.
 *
 * @param {{ visibility?: unknown, hasPassword?: boolean }} options
 * @returns {'public'|'private'|'locked'}
 */
export function roomVisibilityKind(options = {}) {
  if (options.hasPassword) return 'locked';
  if (normalizeRoomVisibility(options.visibility) === ROOM_VISIBILITY_UNLISTED) return 'private';
  return 'public';
}

/**
 * True when this player may publish an unlisted or passworded room.
 * The creator may. In a Discord Activity room the current host may as well.
 *
 * @param {{
 *   userId?: unknown,
 *   creatorId?: unknown,
 *   hostId?: unknown,
 *   visibility?: unknown,
 *   hasPassword?: boolean,
 *   isDiscordActivity?: boolean,
 *   isAdmin?: boolean,
 *   difficulty?: unknown
 * }} options
 * @returns {boolean}
 */
export function canPublishRoom(options = {}) {
  const userId = String(options.userId || '').trim();
  if (!userId) return false;
  const hasPassword = !!options.hasPassword;
  const unlisted = normalizeRoomVisibility(options.visibility) === ROOM_VISIBILITY_UNLISTED;
  if (!unlisted && !hasPassword) return false;
  if (options.isAdmin) return true;
  if (String(options.difficulty || '').trim().toLowerCase() === 'kids') return false;
  if (options.creatorId && String(options.creatorId) === userId) return true;
  if (options.isDiscordActivity && options.hostId && String(options.hostId) === userId) return true;
  return false;
}

/**
 * Confirm copy for the one-way publish action.
 *
 * @param {boolean} hasPassword
 * @returns {string}
 */
export function roomPublishInstructions(hasPassword) {
  const base = 'Making this room public lists it in Join Room for anyone. This is a one-way action and this room cannot be made unlisted again.';
  if (!hasPassword) return base;
  return `${base} The password is removed, so anyone can join without it.`;
}

/** Globe shown for a public room. */
export const ROOM_VISIBILITY_ICON_PUBLIC = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M3 12h18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M12 3c2.6 2.8 3.9 5.8 3.9 9s-1.3 6.2-3.9 9c-2.6-2.8-3.9-5.8-3.9-9s1.3-6.2 3.9-9z" fill="none" stroke="currentColor" stroke-width="2"/></svg>';

/** Eye-off shown for an unlisted (private) room. */
export const ROOM_VISIBILITY_ICON_PRIVATE = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M3 3l18 18"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M10.6 10.7a3 3 0 0 0 4.2 4.2"/><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M9.9 5.2A10.6 10.6 0 0 1 12 5c5.2 0 9.1 4.6 10 7-.35.88-1 1.95-1.9 2.95M6.2 6.3C4.5 7.5 3.2 9.1 2 12c1.1 2.6 5 7 10 7 1.45 0 2.8-.35 4-.95"/></svg>';

/** Padlock shown for a passworded room. */
export const ROOM_VISIBILITY_ICON_LOCKED = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><rect x="5" y="11" width="14" height="9" rx="1.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

/**
 * @param {'public'|'private'|'locked'} kind
 * @returns {string}
 */
export function roomVisibilityIcon(kind) {
  if (kind === 'private') return ROOM_VISIBILITY_ICON_PRIVATE;
  if (kind === 'locked') return ROOM_VISIBILITY_ICON_LOCKED;
  return ROOM_VISIBILITY_ICON_PUBLIC;
}

/**
 * @param {'public'|'private'|'locked'} kind
 * @returns {string}
 */
export function roomVisibilityLabel(kind) {
  if (kind === 'private') return 'Private';
  if (kind === 'locked') return 'Locked';
  return 'Public';
}
